import { useState } from 'react';
import { EmailBodyPreview } from '../components/EmailBodyPreview';
import {
  IconMail,
  IconSearch,
  IconCircleCheck,
  IconAlertCircle,
  IconClock,
  IconBan,
  IconLoader2,
  IconX,
} from '@tabler/icons-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@/components/ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
} from '@/components/ui/pagination';
import api from '../lib/api';
import PageHeader from '../components/PageHeader';
import MainCard from '../components/MainCard';

export function EmailHistoryPage() {
  const [email, setEmail] = useState('');
  const [emails, setEmails] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedEmail, setSelectedEmail] = useState<any | null>(null);
  const [emailDetails, setEmailDetails] = useState<any | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [resending, setResending] = useState(false);

  const handleSearch = async (newPage = 1) => {
    setError('');
    setLoading(true);

    try {
      const res = await api.get(`/email-history/search?email=${email}&page=${newPage}&limit=20`);
      console.log('[Email History] API Response:', res.data);
      setEmails(res.data.emails || []);
      setTotalPages(res.data.pagination?.totalPages || 0);
      setPage(newPage);
    } catch (err: any) {
      console.error('[Email History] Error:', err);
      setError(err.response?.data?.message || 'Search failed');
      setEmails([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSearch(1);
  };

  const handleEmailClick = async (emailItem: any) => {
    setSelectedEmail(emailItem);
    setLoadingDetails(true);
    setEmailDetails(null);

    try {
      const res = await api.get(`/email-history/details/${emailItem.uuid}`);
      setEmailDetails(res.data);
    } catch (err: any) {
      console.error('Failed to load email details:', err);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCloseModal = () => {
    setSelectedEmail(null);
    setEmailDetails(null);
  };

  const handleResendEmail = async () => {
    if (!selectedEmail?.uuid) return;

    const confirmed = window.confirm(
      'Are you sure you want to resend this email?\n\n' +
      `To: ${selectedEmail.to || selectedEmail.email}\n` +
      `Subject: ${selectedEmail.subject || 'No Subject'}`
    );

    if (!confirmed) return;

    setResending(true);
    try {
      await api.post(`/email-history/resend/${selectedEmail.uuid}`);
      alert('✅ Email resent successfully!');
      handleCloseModal();
    } catch (err: any) {
      console.error('Failed to resend email:', err);
      alert(`❌ ${err.response?.data?.message || 'Failed to resend email'}`);
    } finally {
      setResending(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'delivered':
        return <IconCircleCheck size={18} className="text-green-600" />;
      case 'blocked':
      case 'hardbounce':
      case 'softbounce':
        return <IconBan size={18} className="text-red-600" />;
      case 'bounced':
      case 'failed':
        return <IconAlertCircle size={18} className="text-red-600" />;
      case 'sent':
      case 'request':
        return <IconClock size={18} className="text-blue-600" />;
      default:
        return <IconClock size={18} className="text-muted-foreground" />;
    }
  };

  const getStatusVariant = (status: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
    switch (status?.toLowerCase()) {
      case 'delivered':
        return 'default';
      case 'blocked':
      case 'hardbounce':
      case 'softbounce':
      case 'bounced':
      case 'failed':
        return 'destructive';
      case 'sent':
      case 'request':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  return (
    <div>
      <PageHeader
        title="Email History"
        subtitle="Search email delivery history via Brevo API"
      />

      {/* Search Form */}
      <MainCard className="mb-6">
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <Label htmlFor="email-search">Email Address</Label>
            <Input
              id="email-search"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter the email address to search for"
              required
            />
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Button type="submit" size="lg" disabled={loading} className="w-full">
            {loading ? (
              <IconLoader2 size={16} className="animate-spin" />
            ) : (
              <IconSearch size={16} />
            )}
            {loading ? 'Searching...' : 'Search Emails'}
          </Button>
        </form>
      </MainCard>

      {/* Results */}
      {emails.length > 0 && (
        <>
          <h3 className="text-lg font-semibold mb-3">
            Found {emails.length} email{emails.length !== 1 ? 's' : ''} (Page {page} of {totalPages})
          </h3>

          <div className="space-y-3">
            {emails.map((emailItem, index) => (
              <Card
                key={index}
                className="cursor-pointer transition-all hover:shadow-md hover:border-primary"
                onClick={() => handleEmailClick(emailItem)}
              >
                <CardContent className="p-4">
                  <div className="flex gap-3 items-start">
                    <div className="mt-0.5">
                      {getStatusIcon(emailItem.status)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-base font-semibold truncate">
                          {emailItem.subject || 'No Subject'}
                        </h4>
                        <Badge variant={getStatusVariant(emailItem.status)}>
                          {emailItem.status || 'Unknown'}
                        </Badge>
                      </div>
                      
                      <p className="text-sm text-muted-foreground">
                        To: {emailItem.to || emailItem.email || 'N/A'}
                      </p>
                      
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {emailItem.date 
                          ? new Date(emailItem.date).toLocaleString()
                          : 'Date unknown'}
                      </p>

                      {emailItem.messageId && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Message ID: {emailItem.messageId}
                        </p>
                      )}

                      {emailItem.tags && emailItem.tags.length > 0 && (
                        <div className="mt-2 flex gap-1 flex-wrap">
                          {emailItem.tags.map((tag: string, idx: number) => (
                            <Badge key={idx} variant="outline">{tag}</Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center mt-6">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      onClick={() => page > 1 && handleSearch(page - 1)}
                      className={cn(page <= 1 && 'pointer-events-none opacity-50')}
                    />
                  </PaginationItem>
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    const pageNum = i + 1;
                    return (
                      <PaginationItem key={pageNum}>
                        <PaginationLink
                          isActive={pageNum === page}
                          onClick={() => handleSearch(pageNum)}
                        >
                          {pageNum}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  })}
                  <PaginationItem>
                    <PaginationNext
                      onClick={() => page < totalPages && handleSearch(page + 1)}
                      className={cn(page >= totalPages && 'pointer-events-none opacity-50')}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </>
      )}

      {!loading && emails.length === 0 && email && (
        <Alert className="mt-3">
          <AlertDescription>
            No emails found for this address. Try searching with a different email.
          </AlertDescription>
        </Alert>
      )}

      {/* Email Details Modal */}
      <Dialog
        open={!!selectedEmail}
        onOpenChange={(open) => { if (!open) handleCloseModal(); }}
      >
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <IconMail size={18} />
              <DialogTitle>Email Details</DialogTitle>
            </div>
          </DialogHeader>

          {loadingDetails ? (
            <div className="flex justify-center py-8">
              <IconLoader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : emailDetails ? (
            <div className="space-y-4">
              {/* Email Metadata */}
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell className="font-semibold w-[30%]">Subject</TableCell>
                    <TableCell>{emailDetails.subject || selectedEmail?.subject || 'No Subject'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-semibold">To</TableCell>
                    <TableCell>{emailDetails.to?.[0]?.email || selectedEmail?.to || 'N/A'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-semibold">Status</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getStatusIcon(emailDetails.event || selectedEmail?.status)}
                        <Badge variant={getStatusVariant(emailDetails.event || selectedEmail?.status)}>
                          {emailDetails.event || selectedEmail?.status || 'Unknown'}
                        </Badge>
                      </div>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-semibold">Date</TableCell>
                    <TableCell>
                      {emailDetails.date || selectedEmail?.date 
                        ? new Date(emailDetails.date || selectedEmail.date).toLocaleString()
                        : 'N/A'}
                    </TableCell>
                  </TableRow>
                  {emailDetails.messageId && (
                    <TableRow>
                      <TableCell className="font-semibold">Message ID</TableCell>
                      <TableCell className="text-xs break-all">{emailDetails.messageId}</TableCell>
                    </TableRow>
                  )}
                  {emailDetails.uuid && (
                    <TableRow>
                      <TableCell className="font-semibold">UUID</TableCell>
                      <TableCell className="text-xs break-all">{emailDetails.uuid}</TableCell>
                    </TableRow>
                  )}
                  {emailDetails.templateId && (
                    <TableRow>
                      <TableCell className="font-semibold">Template ID</TableCell>
                      <TableCell>{emailDetails.templateId}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              {/* Email Body */}
              {emailDetails.body && (
                <div>
                  <p className="text-sm font-semibold mb-2">Email Content:</p>
                  <EmailBodyPreview html={emailDetails.body} />
                </div>
              )}

              {/* Event Log */}
              {emailDetails.events && emailDetails.events.length > 0 && (
                <div>
                  <p className="text-sm font-semibold mb-2">Event History:</p>
                  <div className="space-y-2">
                    {emailDetails.events.map((event: any, idx: number) => (
                      <div
                        key={idx}
                        className="p-3 rounded-md border border-border bg-background"
                      >
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-2">
                            {getStatusIcon(event.event || event.name)}
                            <Badge variant={getStatusVariant(event.event || event.name)}>
                              {event.event || event.name || 'Event'}
                            </Badge>
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {event.time ? new Date(event.time).toLocaleString() : 'N/A'}
                          </span>
                        </div>
                        {event.reason && (
                          <Alert className="mt-2">
                            <AlertDescription className="text-xs font-semibold">
                              {event.reason}
                            </AlertDescription>
                          </Alert>
                        )}
                        {event.link && (
                          <p className="text-xs text-primary mt-1">
                            Link: {event.link}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <Alert>
              <AlertDescription>No details available</AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseModal}>
              Close
            </Button>
            <Button
              onClick={handleResendEmail}
              disabled={resending}
            >
              {resending ? (
                <IconLoader2 size={14} className="animate-spin" />
              ) : (
                <IconMail size={14} />
              )}
              {resending ? 'Resending...' : 'Resend Email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
